import type { ReactNode } from "react"
import HoverCard from "@/v2-ui/hoverCard"
import Icon from "@/v2-ui/icon"
import cn from "@/v2-ui/utils/utils.cn"

export type LicenseFeatureGroupSectionProps = {
  title: string
  /** One line of plain language that says what the section currently grants. */
  summary: ReactNode
  /**
   * What this kind of thing IS and where it comes from, behind an info mark
   * next to the title — the explanation an operator reads once, kept off
   * the page so the page stays the list.
   */
  about?: ReactNode
  /**
   * The two or three sentences every reader of the list needs, in view
   * the whole time: what a tick means here, and that a header tick covers
   * the future while single ticks do not. Unlike `about`, never hidden.
   */
  legend?: ReactNode
  /** Right-aligned header slot: a count, an action. */
  aside?: ReactNode
  className?: string
  children?: ReactNode
}

// The views of a package — capabilities, content, individual features —
// read as one list of titled sections, each stating in a sentence what it
// grants right now. The sentence is the orientation: an operator should know
// what a package contains without opening anything. The legend beneath says
// how to read the ticks — the part people get wrong — and what the section's
// kind of thing is sits one hover away, behind the info mark.
function LicenseFeatureGroupSection(props: LicenseFeatureGroupSectionProps) {
  const { title, summary, about, legend, aside, className, children } = props
  return (
    <section className={cn("flex flex-col gap-2", className)}>
      <header className="flex items-center gap-3">
        <h3 className="flex shrink-0 items-center gap-1.5 text-sm font-medium">
          {title}
          {about && (
            <HoverCard openDelay={300} closeDelay={120}>
              <HoverCard.Trigger asChild>
                <button
                  type="button"
                  className="flex size-5 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={`What ${title.toLowerCase()} are`}
                  data-feature-code="UI_BackofficeFeatureGroup_Section_About"
                >
                  <Icon src="info" size={12} />
                </button>
              </HoverCard.Trigger>
              <HoverCard.Content
                side="bottom"
                align="start"
                sideOffset={6}
                collisionPadding={12}
                className="w-80 text-xs text-muted-foreground"
              >
                {about}
              </HoverCard.Content>
            </HoverCard>
          )}
        </h3>
        <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {summary}
        </p>
        {aside && (
          <div className="flex shrink-0 items-center gap-2">{aside}</div>
        )}
      </header>
      {legend && (
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          {legend}
        </p>
      )}
      {children}
    </section>
  )
}

export default LicenseFeatureGroupSection
